import moment from "moment-timezone";

// Time off plan rules, read the same way as the backend (clock-in src/Util/TimeOffRules.ts)
// and settings at accrual. Calendar dates are compared in UTC on their YYYY-MM-DD part.

const TOLERANCE = 1e-6;

export const toUtcDate = (value) => {
	if (value === undefined || value === null || value === "") {
		return null;
	}
	const day = typeof value === "string"
		? value.slice(0, 10)
		: moment(value).format("YYYY-MM-DD");
	const date = moment.utc(day, "YYYY-MM-DD", true);
	return date.isValid() ? date : null;
};

// the date tiers and probation count from: hire date or seniority date, as the plan says
export const getTierStartDate = (plan, user) => {
	switch (plan?.tierBy) {
		case "hire-date":
			return toUtcDate(user?.hiringDate);
		case "seniority":
			return toUtcDate(user?.dateOfSeniority);
		default:
			return null;
	}
};

// index of the employee's tier on `asOf`: whole years reached, both bounds inclusive; -1 if none
export const getTierIndex = (plan, user, asOf) => {
	if (!Array.isArray(plan?.tiers)) {
		return -1;
	}
	const start = getTierStartDate(plan, user);
	const date = toUtcDate(asOf);
	if (!start || !date) {
		return plan.tiers.findIndex((tier) => tier.start === null || tier.start === undefined);
	}
	const years = date.diff(start, "years");
	return plan.tiers.findIndex((tier) => {
		const from = tier.start === null || tier.start === undefined ? null : Number(tier.start);
		const to = tier.end === null || tier.end === undefined ? null : Number(tier.end);
		return (from === null || years >= from) && (to === null || years <= to);
	});
};

export const getMaxRequestedHours = (plan, user, asOf) => {
	if (!Array.isArray(plan?.maxRequestedHours)) {
		return undefined;
	}
	const tierIndex = getTierIndex(plan, user, asOf);
	if (tierIndex === -1) {
		return undefined;
	}
	const value = Number(plan.maxRequestedHours[tierIndex]?.value);
	return value ? value : undefined;
};

// in probation on `date` while prohibition.value >= days since the tier date; no date: not in probation
export const isInProbation = (plan, user, date) => {
	const value = plan?.prohibition?.value;
	if (value === undefined || value === null || value === "" || isNaN(Number(value))) {
		return false;
	}
	const start = getTierStartDate(plan, user);
	const day = toUtcDate(date);
	if (!start || !day) {
		return false;
	}
	return Number(value) >= day.diff(start, "days");
};

// how far below zero the balance may go: |minBalance| when negative, else 0
export const getOverdraft = (plan) => {
	const minBalance = Number(plan?.minBalance);
	return isNaN(minBalance) ? 0 : Math.abs(Math.min(minBalance, 0));
};

export const isMultipleOfMinRequested = (plan, hours) => {
	const min = Number(plan?.minRequestedHours);
	if (!min || min < 0) {
		return true;
	}
	return Math.abs(Math.round(hours / min) * min - hours) <= TOLERANCE;
};

// the plan's minimum balance: what has to stay after the time off (negative: how far the balance
// may go below zero), as clock-in getMinBalance
export const getMinBalance = (plan) => {
	const minBalance = Number(plan?.minBalance);
	return isNaN(minBalance) ? 0 : minBalance;
};

// true when taking `requested` leaves at least the plan's minimum balance of `available`
// (clock-in fitsBalance): a positive minimum has to stay, a negative one allows going below zero
export const fitsBalance = (plan, available, requested) => {
	return requested <= (Number(available) || 0) - getMinBalance(plan) + TOLERANCE;
};

// true when `requested` on top of what the plan year already `taken` stays within `max` (none: always),
// as clock-in fitsMaxTaken
export const fitsMaxTaken = (max, taken, requested) => {
	return max === undefined || (Number(taken) || 0) + requested <= max + TOLERANCE;
};

const takenOnPlan = (userTimeOffPlan) => {
	// locked periods are in `bucket.consumed`; time sheets not yet locked and pending requests are
	// pending items. Paid out is not taken (clock-in leaves payouts out too)
	const consumed = Number(userTimeOffPlan?.bucket?.consumed) || 0;
	const pending = (Array.isArray(userTimeOffPlan?.pendingItems) ? userTimeOffPlan.pendingItems : [])
		.filter((item) => item?.type !== "payout")
		.reduce((total, item) => total + (Number(item?.total) || 0), 0);
	return consumed + pending;
};

// Hours of the plan year already taken on `userTimeOffPlan`, for "Maximum hours allowed to be taken",
// read from the plans the screen loaded (`/users/:id/time-off`), as clock-in TimeOffTakenService:
// a plan opened by a transfer adds the plans before it (the `transfer` log's plan, else the plan whose
// `transferTo` is this one), stopping at a plan closed by the year end. A plan not in `allPlans` is
// not counted, so the save can still refuse what the screen let through, never the other way round.
export const getHoursTaken = (userTimeOffPlan, allPlans = []) => {
	if (!userTimeOffPlan) {
		return 0;
	}
	let total = takenOnPlan(userTimeOffPlan);
	const seen = new Set([userTimeOffPlan.id]);
	let plan = userTimeOffPlan;
	for (let hop = 0; plan && hop < 20; hop++) {
		const opening = (plan.logs || []).find((log) => log?.type === "transfer");
		if (!opening) {
			break;
		}
		const fromId = opening.meta?.transferPlan?.plan?.id;
		const current = plan;
		const previous = (allPlans || []).find((item) => fromId ? item?.id === fromId : item?.transferTo === current.id);
		if (!previous || seen.has(previous.id) || (previous.logs || []).some((log) => log?.type === "close-current-year")) {
			break;
		}
		seen.add(previous.id);
		total += takenOnPlan(previous);
		plan = previous;
	}
	return parseFloat(total.toFixed(5));
};
